import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    CameraIcon,
    ArrowPathIcon,
    DocumentTextIcon,
    PhotoIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    ListBulletIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { Link, useSearchParams } from 'react-router-dom'
import PageMasthead from '../../components/PageMasthead'
import {
    ScreenshotStatus,
    useGetScreenshotContentSummaryQuery,
    ScreenshotContentSummary,
    ScreenshotAttempt,
    CookiePlatform,
    useGetCookiesQuery
} from '../../api/client'
import Spinner from '../../components/Spinner'
import ScreenshotDetailsModal from '../../components/screenshots/ScreenshotDetailsModal'
import ScreenshotImageModal from '../../components/screenshots/ScreenshotImageModal'
import ContentAttemptsModal from '../../components/screenshots/ContentAttemptsModal'
import { faIconName } from '../../util/classNames'

const STORAGE_KEY = 'screenshotListFilters'
const POLL_INTERVAL = 30000 // 30 seconds

type StatusFilter = ScreenshotStatus | 'ALL'

type Filters = {
    status: StatusFilter
    page: number
    pageSize: number
}

const DEFAULT_FILTERS: Filters = {
    status: 'ALL',
    page: 1,
    pageSize: 20
}

const ScreenshotListPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams()

    // Modal state
    const [selectedScreenshot, setSelectedScreenshot] = useState<ScreenshotAttempt | null>(null)
    const [detailsModalOpen, setDetailsModalOpen] = useState(false)
    const [imageModalOpen, setImageModalOpen] = useState(false)
    const [attemptsModalOpen, setAttemptsModalOpen] = useState(false)
    const [selectedContentId, setSelectedContentId] = useState<number | null>(null)
    const [selectedContentText, setSelectedContentText] = useState<string | null>(null)

    // Filter state initialization
    const getInitialFilters = (): Filters => {
        if (searchParams.has('status')) {
            return {
                status: (searchParams.get('status') as StatusFilter) || DEFAULT_FILTERS.status,
                page: parseInt(searchParams.get('page') || '1'),
                pageSize: parseInt(searchParams.get('pageSize') || '20')
            }
        }

        const saved = localStorage.getItem(STORAGE_KEY)
        if (saved) {
            try {
                return JSON.parse(saved)
            } catch {
                return DEFAULT_FILTERS
            }
        }

        return DEFAULT_FILTERS
    }

    const [filters, setFilters] = useState<Filters>(getInitialFilters)

    // GraphQL query with polling - using grouped view
    const { data, loading, error, refetch } = useGetScreenshotContentSummaryQuery({
        variables: {
            offset: (filters.page - 1) * filters.pageSize,
            limit: filters.pageSize,
            status: filters.status === 'ALL' ? undefined : filters.status
        },
        pollInterval: POLL_INTERVAL,
        errorPolicy: 'all'
    })

    const summaries = data?.getScreenshotContentSummary?.items || []
    const totalCount = data?.getScreenshotContentSummary?.total || 0
    const totalPages = Math.max(1, Math.ceil(totalCount / filters.pageSize))

    // Check for missing cookies (FB and IG only)
    const facebookCookiesQuery = useGetCookiesQuery({ variables: { platform: CookiePlatform.Facebook } })
    const instagramCookiesQuery = useGetCookiesQuery({ variables: { platform: CookiePlatform.Instagram } })

    const cookiesLoaded = !facebookCookiesQuery.loading && !instagramCookiesQuery.loading

    const platformsWithoutActiveCookies = useMemo(() => {
        // Don't show warning until queries have loaded
        if (!cookiesLoaded) return []

        const missing: string[] = []
        const fbCookies = facebookCookiesQuery.data?.getCookies || []
        const igCookies = instagramCookiesQuery.data?.getCookies || []

        if (!fbCookies.some(c => c.status === 'ACTIVE')) {
            missing.push('Facebook')
        }
        if (!igCookies.some(c => c.status === 'ACTIVE')) {
            missing.push('Instagram')
        }
        return missing
    }, [cookiesLoaded, facebookCookiesQuery.data, instagramCookiesQuery.data])

    // Sync filters to localStorage and URL
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
        const params = new URLSearchParams()
        params.set('status', filters.status)
        params.set('page', String(filters.page))
        params.set('pageSize', String(filters.pageSize))
        setSearchParams(params, { replace: true })
    }, [filters, setSearchParams])

    const updateFilter = (update: Partial<Filters>) => {
        setFilters((prev) => ({
            ...prev,
            ...update,
            // Reset to page 1 when changing filters (but not when just changing page)
            page: update.page !== undefined ? update.page : 1
        }))
    }

    const handleRefresh = useCallback(() => {
        refetch()
    }, [refetch])

    const openDetailsModal = (screenshot: ScreenshotAttempt) => {
        setSelectedScreenshot(screenshot)
        setDetailsModalOpen(true)
    }

    const openImageModal = (screenshot: ScreenshotAttempt) => {
        setSelectedScreenshot(screenshot)
        setImageModalOpen(true)
    }

    const openAttemptsModal = (contentId: number, contentText?: string | null) => {
        setSelectedContentId(contentId)
        setSelectedContentText(contentText || null)
        setAttemptsModalOpen(true)
    }

    const renderDate = (value?: string | null) => {
        if (!value) return <span className="text-gray-400">-</span>
        try {
            const d = parseISO(value)
            return (
                <div className="text-sm text-gray-900">
                    {formatDistanceToNow(d, { addSuffix: true })}
                    <div className="text-xs text-gray-500">{d.toLocaleString()}</div>
                </div>
            )
        } catch {
            return <span className="text-gray-500">{value}</span>
        }
    }

    const renderStatusCounts = (summary: ScreenshotContentSummary) => {
        return (
            <div className="flex items-center space-x-3 text-sm">
                {summary.successCount > 0 && (
                    <span className="inline-flex items-center text-green-700">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        {summary.successCount}
                    </span>
                )}
                {summary.failedCount > 0 && (
                    <span className="inline-flex items-center text-amber-700">
                        <XCircleIcon className="h-4 w-4 mr-1" />
                        {summary.failedCount}
                    </span>
                )}
                {summary.pendingCount > 0 && (
                    <span className="inline-flex items-center text-yellow-700">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        {summary.pendingCount}
                    </span>
                )}
            </div>
        )
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">
                                    Error loading screenshot data
                                </h3>
                                <div className="mt-2 text-sm text-red-700">{String(error)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <PageMasthead
                title="Screenshots"
                tabTitle="Screenshots"
                subtitle={`${totalCount} content item${totalCount !== 1 ? 's' : ''} with screenshots`}
                actions={
                    <div className="flex items-center space-x-4">
                        <div className="text-sm text-ink-500">Auto-refreshes every 30 seconds</div>
                        <select
                            value={filters.status}
                            onChange={(e) => updateFilter({ status: e.target.value as StatusFilter })}
                            className="block border border-ink-200 rounded-md shadow-sm text-sm text-ink-700 focus:ring-arwen-500 focus:border-arwen-500 pl-3 pr-8 py-2"
                        >
                            <option value="ALL">All statuses</option>
                            <option value={ScreenshotStatus.Success}>Success only</option>
                            <option value={ScreenshotStatus.Failed}>Error only</option>
                            <option value={ScreenshotStatus.Pending}>Pending only</option>
                        </select>
                        <button
                            onClick={handleRefresh}
                            className="inline-flex items-center px-3 py-2 border border-ink-200 shadow-sm text-xs font-medium rounded-md text-ink-700 bg-white hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arwen-500"
                        >
                            <ArrowPathIcon className="h-4 w-4 mr-1" />
                            Refresh
                        </button>
                    </div>
                }
            />

            <p className="px-6 pt-4 font-sans text-sm text-ink-500">
                View screenshot attempts grouped by content. Shows success/failure counts per
                content item.
            </p>

            {platformsWithoutActiveCookies.length > 0 && (
                <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-medium text-amber-800">
                            No active cookies for {platformsWithoutActiveCookies.join(' or ')}
                        </div>
                        <div className="text-sm text-amber-700 mt-1">
                            Screenshots cannot be taken for these platforms until valid cookies are added.
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-col">
                {loading && summaries.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                        <Spinner size={6} />
                    </div>
                ) : summaries.length === 0 ? (
                    <div className="text-center py-12">
                        <CameraIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-2 text-sm text-gray-500">
                            No screenshots found. Try adjusting your filters.
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4 p-6">
                        {summaries.map((summary: ScreenshotContentSummary) => (
                            <div
                                key={summary.contentId}
                                className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex"
                            >
                                {/* Failure indicator stripe */}
                                {summary.failedCount > 0 && (
                                    <div className="w-1 bg-amber-500 flex-shrink-0" />
                                )}

                                <div className="flex flex-1">
                                    {/* Screenshot thumbnail */}
                                    <div className="flex-shrink-0 w-56 bg-gray-100 flex items-center justify-center">
                                        {summary.latestScreenshot?.s3Url ? (
                                            <button
                                                onClick={() =>
                                                    openImageModal(
                                                        summary.latestScreenshot as ScreenshotAttempt
                                                    )
                                                }
                                                className="group relative w-full h-full min-h-[120px]"
                                                title="Click to view full size"
                                            >
                                                <img
                                                    src={summary.latestScreenshot.s3Url}
                                                    alt={`Screenshot for content ${summary.contentId}`}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        const target = e.currentTarget
                                                        target.style.display = 'none'
                                                        const fallback = target.parentElement?.querySelector('.fallback-text')
                                                        if (fallback) fallback.classList.remove('hidden')
                                                    }}
                                                />
                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity flex items-center justify-center">
                                                    <PhotoIcon className="h-8 w-8 text-white opacity-0 group-hover:opacity-100" />
                                                </div>
                                                <span className="fallback-text hidden text-xs text-gray-400 italic">
                                                    Not available
                                                </span>
                                            </button>
                                        ) : (
                                            <div className="flex items-center justify-center h-full min-h-[120px] text-gray-400">
                                                <CameraIcon className="h-10 w-10" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Content details - columnar layout */}
                                    <div className="flex-grow p-3 flex items-start">
                                        {/* Column 1: Content ID & Account */}
                                        <div className="w-48 flex-shrink-0">
                                            <div className="flex items-center gap-2">
                                                {summary.socialMediaService && (
                                                    <i
                                                        className={`fa-brands ${faIconName(summary.socialMediaService.toLowerCase(), true)} text-lg`}
                                                        title={summary.socialMediaService}
                                                    />
                                                )}
                                                <Link
                                                    to={`/content/${summary.contentId}`}
                                                    className="text-arwen-600 hover:text-arwen-700 hover:underline font-medium text-lg"
                                                >
                                                    #{summary.contentId}
                                                </Link>
                                            </div>
                                            {summary.accountName && (
                                                <div className="text-sm text-gray-700 mt-1 font-medium truncate" title={summary.accountName}>
                                                    {summary.accountName}
                                                </div>
                                            )}
                                            {summary.authorName && (
                                                <div className="text-sm text-gray-500 mt-0.5 truncate" title={summary.authorName}>
                                                    by {summary.authorName}
                                                </div>
                                            )}
                                        </div>

                                        {/* Column 2: Content text */}
                                        <div className="flex-grow px-4 min-w-0">
                                            {summary.contentText ? (
                                                <div
                                                    className="text-gray-800 line-clamp-3"
                                                    title={summary.contentText}
                                                >
                                                    {summary.contentText}
                                                </div>
                                            ) : (
                                                <div className="text-gray-400 text-sm italic">No content text</div>
                                            )}
                                        </div>

                                        {/* Column 3: Status counts */}
                                        <div className="w-32 flex-shrink-0 px-4">
                                            {renderStatusCounts(summary)}
                                            <div className="text-xs text-gray-500 mt-1">
                                                {summary.totalAttempts} attempt{summary.totalAttempts !== 1 ? 's' : ''}
                                            </div>
                                        </div>

                                        {/* Column 4: Date */}
                                        <div className="w-40 flex-shrink-0 px-4 text-sm text-gray-500">
                                            {renderDate(summary.latestCreatedAt)}
                                        </div>

                                        {/* Column 5: Actions */}
                                        <div className="w-40 flex-shrink-0 flex flex-col gap-2">
                                            <button
                                                onClick={() =>
                                                    openAttemptsModal(
                                                        summary.contentId,
                                                        summary.contentText
                                                    )
                                                }
                                                className="inline-flex items-center text-sm text-arwen-600 hover:text-arwen-700"
                                                title="View all screenshot attempts"
                                            >
                                                <ListBulletIcon className="h-4 w-4 mr-1" />
                                                All attempts
                                            </button>
                                            {summary.latestScreenshot &&
                                                (summary.latestScreenshot.errorMessage ||
                                                    summary.latestScreenshot.logs) && (
                                                    <button
                                                        onClick={() =>
                                                            openDetailsModal(
                                                                summary.latestScreenshot as ScreenshotAttempt
                                                            )
                                                        }
                                                        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
                                                        title="View latest error message and logs"
                                                    >
                                                        <DocumentTextIcon className="h-4 w-4 mr-1" />
                                                        Logs
                                                    </button>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {totalCount > 0 && (
                    <div className="flex items-center justify-between text-sm text-ink-500 px-6 py-3 bg-white border-t border-ink-200">
                        <div>
                            Showing {(filters.page - 1) * filters.pageSize + 1}-
                            {Math.min(filters.page * filters.pageSize, totalCount)} of {totalCount}{' '}
                            content items
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                className="rounded-md border border-ink-200 px-3 py-1 text-ink-700 bg-white hover:bg-ink-50 disabled:opacity-50"
                                onClick={() => updateFilter({ page: Math.max(1, filters.page - 1) })}
                                disabled={filters.page === 1}
                            >
                                Previous
                            </button>
                            <span>
                                Page {filters.page} of {totalPages}
                            </span>
                            <button
                                className="rounded-md border border-ink-200 px-3 py-1 text-ink-700 bg-white hover:bg-ink-50 disabled:opacity-50"
                                onClick={() =>
                                    updateFilter({ page: Math.min(totalPages, filters.page + 1) })
                                }
                                disabled={filters.page === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            <ScreenshotDetailsModal
                open={detailsModalOpen}
                setOpen={setDetailsModalOpen}
                screenshot={selectedScreenshot}
            />
            <ScreenshotImageModal
                open={imageModalOpen}
                setOpen={setImageModalOpen}
                imageUrl={selectedScreenshot?.s3Url || null}
                contentId={selectedScreenshot?.contentId || null}
            />
            <ContentAttemptsModal
                open={attemptsModalOpen}
                setOpen={setAttemptsModalOpen}
                contentId={selectedContentId}
                contentText={selectedContentText}
            />
        </div>
    )
}

export default ScreenshotListPage
